/**
 * Issue Contract — Canonical validation issue model for the governance pipeline.
 *
 * ARCHITECTURE v4.0 — Phase 1, Step 1.0
 *
 * This replaces the scattered GovernanceIssue, UnifiedIssue, VisualValidationIssue,
 * and ValidationError types with a single, deterministic issue contract.
 *
 * Design principles:
 * - N5: Issue determinism — same input → same issues (signature-based dedup)
 * - N4: Issue Contract governs gates; scoring never overrides blockers
 * - N6: No silent data correction in production
 */

// ─── Issue Kinds ──────────────────────────────────────────────────────────────

/** Issue kind determines gate behavior */
export type IssueKind = 'blocker' | 'coach' | 'info'

// ─── Issue Code Registry ──────────────────────────────────────────────────────
// RESOLVES: CONFLICT-11 (Issue codes defined but never enumerated)
//
// Canonical registry of all issue codes emitted by pipeline stages.
// Provides compile-time safety and documentation for each code.

export const ISSUE_CODES = {
  // ── Schema Stage (Stage 1) ──────────────────────────────────────────────
  SCHEMA_MISSING_FIELD: 'SCHEMA_MISSING_FIELD',
  SCHEMA_INVALID_TYPE: 'SCHEMA_INVALID_TYPE',
  SCHEMA_INVALID_VALUE: 'SCHEMA_INVALID_VALUE',
  SCHEMA_MISSING_OPTIONS: 'SCHEMA_MISSING_OPTIONS',
  SCHEMA_WRONG_OPTION_COUNT: 'SCHEMA_WRONG_OPTION_COUNT',
  SCHEMA_MISSING_CORRECT_ANSWER: 'SCHEMA_MISSING_CORRECT_ANSWER',
  SCHEMA_CORRECT_ANSWER_OUT_OF_RANGE: 'SCHEMA_CORRECT_ANSWER_OUT_OF_RANGE',
  SCHEMA_MISSING_FAMILY_ID: 'SCHEMA_MISSING_FAMILY_ID',
  SCHEMA_UNKNOWN_FAMILY_ID: 'SCHEMA_UNKNOWN_FAMILY_ID',
  SCHEMA_MISSING_TIER: 'SCHEMA_MISSING_TIER',
  SCHEMA_TIER_OUT_OF_RANGE: 'SCHEMA_TIER_OUT_OF_RANGE',

  // ── Type Stage (Stage 1b) ──────────────────────────────────────────────
  TYPE_UNKNOWN_QUESTION_TYPE: 'TYPE_UNKNOWN_QUESTION_TYPE',
  TYPE_MISSING_DATA_FIELD: 'TYPE_MISSING_DATA_FIELD',
  TYPE_INVALID_DATA_STRUCTURE: 'TYPE_INVALID_DATA_STRUCTURE',

  // ── Content Stage (Stage 3) ────────────────────────────────────────────
  CONTENT_FORBIDDEN_VOCAB: 'CONTENT_FORBIDDEN_VOCAB',
  CONTENT_QUESTION_TOO_LONG: 'CONTENT_QUESTION_TOO_LONG',
  CONTENT_EXPLANATION_TOO_SHORT: 'CONTENT_EXPLANATION_TOO_SHORT',
  CONTENT_GENERIC_EXPLANATION: 'CONTENT_GENERIC_EXPLANATION',
  CONTENT_EXPLANATION_NOT_DIDACTIC: 'CONTENT_EXPLANATION_NOT_DIDACTIC',
  CONTENT_MISSING_RULE_REF: 'CONTENT_MISSING_RULE_REF',
  CONTENT_MISSING_CHECK_STRATEGY: 'CONTENT_MISSING_CHECK_STRATEGY',
  CONTENT_MISSING_DISTRACTOR_EXPLANATION:
    'CONTENT_MISSING_DISTRACTOR_EXPLANATION',
  CONTENT_DISTRACTOR_TOO_SHORT: 'CONTENT_DISTRACTOR_TOO_SHORT',
  CONTENT_GENERIC_DISTRACTOR: 'CONTENT_GENERIC_DISTRACTOR',
  CONTENT_NO_MISCONCEPTION_REF: 'CONTENT_NO_MISCONCEPTION_REF',
  CONTENT_MISSING_LATEX: 'CONTENT_MISSING_LATEX',
  CONTENT_DOT_DECIMAL: 'CONTENT_DOT_DECIMAL',
  CONTENT_EURO_DOT: 'CONTENT_EURO_DOT',
  CONTENT_MISSING_SUPERSCRIPT: 'CONTENT_MISSING_SUPERSCRIPT',

  // ── Visual Stage (Stage 4) ─────────────────────────────────────────────
  VISUAL_MISSING_REQUIRED: 'VISUAL_MISSING_REQUIRED',
  VISUAL_UNKNOWN_TYPE: 'VISUAL_UNKNOWN_TYPE',
  VISUAL_INVALID_DATA: 'VISUAL_INVALID_DATA',
  VISUAL_SCHEMA_MISMATCH: 'VISUAL_SCHEMA_MISMATCH',

  // ── Support Stage (Stage 5) ────────────────────────────────────────────
  SUPPORT_MISSING_EXPLANATION: 'SUPPORT_MISSING_EXPLANATION',
  SUPPORT_MISSING_HINT: 'SUPPORT_MISSING_HINT',
  SUPPORT_MISSING_VISUAL_AID: 'SUPPORT_MISSING_VISUAL_AID',

  // ── Graph Stage (Stage 2) ──────────────────────────────────────────────
  GRAPH_UNKNOWN_PREREQUISITE: 'GRAPH_UNKNOWN_PREREQUISITE',
  GRAPH_CYCLE_DETECTED: 'GRAPH_CYCLE_DETECTED',
  GRAPH_ORPHAN_NODE: 'GRAPH_ORPHAN_NODE',
  GRAPH_TIER_MISMATCH: 'GRAPH_TIER_MISMATCH',
} as const

/** Type-safe issue code */
export type IssueCode = (typeof ISSUE_CODES)[keyof typeof ISSUE_CODES]

/** Validation domains map to pipeline stages */
export type IssueDomain =
  | 'schema' // Stage 1: structural validation
  | 'type' // Stage 1b: question type validation
  | 'graph' // Stage 2: LDG/prerequisite validation
  | 'content' // Stage 3: content quality validation
  | 'visual' // Stage 4: visual aid validation
  | 'support' // Stage 5: support/hint validation
  | 'scoring' // Stage 6: score computation
  | 'gate' // Stage 7: gate logic

/** Pipeline stage numbers (1-7) */
export type PipelineStage = 1 | 2 | 3 | 4 | 5 | 6 | 7

// ─── Core Issue Contract ──────────────────────────────────────────────────────

/**
 * ValidationIssue — The canonical issue type for the entire governance pipeline.
 *
 * Every validator in every stage produces ValidationIssue objects.
 * Issues are deduplicated by signature and aggregated by the pipeline orchestrator.
 */
export interface ValidationIssue {
  /** Machine-readable issue code (e.g., "SCHEMA_MISSING_FIELD", "CONTENT_GENERIC_EXPLANATION") */
  code: string

  /**
   * Deterministic signature for deduplication.
   * Format: `${domain}::${code}::${field ?? ''}::${normalizedContext}`
   * Same input MUST produce same signature (N5).
   */
  signature: string

  /** Issue kind determines gate behavior */
  kind: IssueKind

  /** Which validation domain produced this issue */
  domain: IssueDomain

  /** Human-readable message (Dutch or English depending on context) */
  message: string

  /** Which field triggered the issue (e.g., "feedback.explanationText", "options[2]") */
  field?: string

  /** What was expected */
  expected?: string

  /** What was actually found */
  actual?: string

  /** Pipeline stage that produced this issue (1-7) */
  stage: PipelineStage

  /**
   * Severity score (0-100).
   * Higher = more severe.
   * Blockers: 70-100, Coach: 30-69, Info: 0-29
   */
  severity: number

  /** Optional fix suggestion */
  suggestion?: string

  /** Optional: which question triggered this */
  questionId?: string

  /** Optional: which family triggered this */
  familyId?: string
}

// ─── Gate Logic ───────────────────────────────────────────────────────────────

/** Gate verdict for a single question */
export type GateVerdict = 'PASS' | 'FAIL' | 'REVIEW_NEEDED'

/** Gate thresholds (configurable via policy) */
export interface GateThresholds {
  /** No blockers + score ≥ this → production eligible */
  productionMinScore: number

  /** No blockers + no high-severity coach + score ≥ this → assessment eligible */
  assessmentMinScore: number

  /** Coach severity ≥ this → REVIEW_NEEDED */
  reviewCoachSeverityThreshold: number

  /** Minimum questions per family for assessment pool */
  assessmentMinQuestionsPerFamily: number
}

/** Default gate thresholds */
export const DEFAULT_GATE_THRESHOLDS: GateThresholds = {
  productionMinScore: 60,
  assessmentMinScore: 75,
  reviewCoachSeverityThreshold: 50,
  assessmentMinQuestionsPerFamily: 3,
}

/** Result of gate evaluation for a single question */
export interface GateResult {
  /** Overall verdict */
  verdict: GateVerdict

  /** Is this question eligible for production (practice mode)? */
  productionEligible: boolean

  /** Is this question eligible for assessment (CITO-style tests)? */
  assessmentEligible: boolean

  /** Does this question need human review? */
  reviewNeeded: boolean

  /** Computed quality score (0-100) */
  score: number

  /** All issues found */
  issues: ValidationIssue[]

  /** Issue counts by kind */
  counts: {
    blockers: number
    coaches: number
    infos: number
    total: number
  }

  /** Reason for verdict (human-readable) */
  reason: string
}

// ─── Pipeline Result ──────────────────────────────────────────────────────────

/** Result of the full pipeline for a single question */
export interface PipelineResult {
  questionId: string
  familyId: string

  /** Gate result (final verdict) */
  gate: GateResult

  /** Per-stage issue breakdown */
  stageResults: Record<PipelineStage, ValidationIssue[]>

  /** Per-domain scores (0-100) */
  domainScores: Record<IssueDomain, number>

  /** Detected cognitive level */
  cognitiveLevel?: string

  /** Timestamp of validation run */
  timestamp: number
}

/** Result of a batch pipeline run */
export interface PipelineBatchResult {
  generatedAt: string
  totalQuestions: number

  /** Aggregate counts */
  verdicts: Record<GateVerdict, number>
  productionEligible: number
  assessmentEligible: number
  reviewNeeded: number

  /** Average score */
  avgScore: number

  /** Issue distribution */
  issuesByDomain: Record<IssueDomain, number>
  issuesByKind: Record<IssueKind, number>

  /** Per-question results */
  results: PipelineResult[]
}

// ─── Issue Construction Helpers ───────────────────────────────────────────────

/**
 * Create a deterministic signature for an issue.
 * Ensures N5: same input → same issues.
 */
export function createIssueSignature(
  domain: IssueDomain,
  code: string,
  field?: string,
  context?: string,
): string {
  const normalizedContext = (context ?? '').trim().toLowerCase().slice(0, 100)
  return `${domain}::${code}::${field ?? ''}::${normalizedContext}`
}

/**
 * Create a blocker issue (blocks production + assessment).
 */
export function blocker(
  domain: IssueDomain,
  stage: PipelineStage,
  code: string,
  message: string,
  opts?: {
    field?: string
    expected?: string
    actual?: string
    suggestion?: string
    questionId?: string
    familyId?: string
    severity?: number
  },
): ValidationIssue {
  return {
    code,
    signature: createIssueSignature(domain, code, opts?.field, message),
    kind: 'blocker',
    domain,
    message,
    stage,
    severity: opts?.severity ?? 80,
    field: opts?.field,
    expected: opts?.expected,
    actual: opts?.actual,
    suggestion: opts?.suggestion,
    questionId: opts?.questionId,
    familyId: opts?.familyId,
  }
}

/**
 * Create a coach issue (quality improvement suggestion, may trigger review).
 */
export function coach(
  domain: IssueDomain,
  stage: PipelineStage,
  code: string,
  message: string,
  opts?: {
    field?: string
    expected?: string
    actual?: string
    suggestion?: string
    questionId?: string
    familyId?: string
    severity?: number
  },
): ValidationIssue {
  return {
    code,
    signature: createIssueSignature(domain, code, opts?.field, message),
    kind: 'coach',
    domain,
    message,
    stage,
    severity: opts?.severity ?? 45,
    field: opts?.field,
    expected: opts?.expected,
    actual: opts?.actual,
    suggestion: opts?.suggestion,
    questionId: opts?.questionId,
    familyId: opts?.familyId,
  }
}

/**
 * Create an info issue (informational, no gate impact).
 */
export function info(
  domain: IssueDomain,
  stage: PipelineStage,
  code: string,
  message: string,
  opts?: {
    field?: string
    suggestion?: string
    questionId?: string
    familyId?: string
    severity?: number
  },
): ValidationIssue {
  return {
    code,
    signature: createIssueSignature(domain, code, opts?.field, message),
    kind: 'info',
    domain,
    message,
    stage,
    severity: opts?.severity ?? 10,
    field: opts?.field,
    suggestion: opts?.suggestion,
    questionId: opts?.questionId,
    familyId: opts?.familyId,
  }
}

// ─── Gate Evaluation ──────────────────────────────────────────────────────────

/**
 * Evaluate gate logic for a set of issues.
 *
 * Gate rules (from architecture doc):
 * - productionGate: no blockers + score ≥ 60
 * - assessmentGate: no blockers + no high-severity coach + score ≥ 75 + type eligible
 * - reviewGate: coach severity ≥ 50 → REVIEW_NEEDED
 */
export function evaluateGate(
  issues: ValidationIssue[],
  thresholds: GateThresholds = DEFAULT_GATE_THRESHOLDS,
): GateResult {
  const blockers = issues.filter((i) => i.kind === 'blocker')
  const coaches = issues.filter((i) => i.kind === 'coach')
  const infos = issues.filter((i) => i.kind === 'info')

  // Score: start at 100, subtract penalties
  const penalty = issues.reduce((sum, issue) => {
    switch (issue.kind) {
      case 'blocker':
        return sum + 30
      case 'coach':
        return sum + Math.round(issue.severity / 4)
      case 'info':
        return sum + 2
      default:
        return sum
    }
  }, 0)
  const score = Math.max(0, 100 - penalty)

  // Gate evaluation
  const hasBlockers = blockers.length > 0
  const hasHighSeverityCoach = coaches.some(
    (c) => c.severity >= thresholds.reviewCoachSeverityThreshold,
  )

  const productionEligible =
    !hasBlockers && score >= thresholds.productionMinScore
  const assessmentEligible =
    !hasBlockers &&
    !hasHighSeverityCoach &&
    score >= thresholds.assessmentMinScore
  const reviewNeeded = hasHighSeverityCoach

  // Determine verdict
  let verdict: GateVerdict
  let reason: string

  if (hasBlockers) {
    verdict = 'FAIL'
    reason = `${blockers.length} blocker(s) found: ${blockers.map((b) => b.code).join(', ')}`
  } else if (reviewNeeded) {
    verdict = 'REVIEW_NEEDED'
    reason = `High-severity coach issue(s) require review (score: ${score})`
  } else if (score < thresholds.productionMinScore) {
    verdict = 'FAIL'
    reason = `Score ${score} below production threshold (${thresholds.productionMinScore})`
  } else {
    verdict = 'PASS'
    reason = `Score ${score}, no blockers, ${productionEligible ? 'production' : ''}${assessmentEligible ? '+assessment' : ''} eligible`
  }

  return {
    verdict,
    productionEligible,
    assessmentEligible,
    reviewNeeded,
    score,
    issues,
    counts: {
      blockers: blockers.length,
      coaches: coaches.length,
      infos: infos.length,
      total: issues.length,
    },
    reason,
  }
}

// ─── Deduplication ────────────────────────────────────────────────────────────

/**
 * Deduplicate issues by signature.
 * When duplicates exist, keep the highest-severity one.
 */
export function deduplicateIssues(
  issues: ValidationIssue[],
): ValidationIssue[] {
  const seen = new Map<string, ValidationIssue>()

  for (const issue of issues) {
    const existing = seen.get(issue.signature)
    if (!existing || issue.severity > existing.severity) {
      seen.set(issue.signature, issue)
    }
  }

  return Array.from(seen.values())
}
